import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Close as CloseIcon, EventBusy as EventBusyIcon } from '@mui/icons-material';
import { addAbsences, deleteAbsence, getAbsences } from '../../utils/absenceService';
import { formatDateYYYYMMDD, parseLocalDate } from '../../utils/dateHelpers';
import { MONTH_NAMES } from '../monthly/format';
import { segmentedSx } from '../monthly/MultiSelectFilter';
import { DAY_COLORS, GREEN, INK, LINE, MUTED } from '../home/homeTheme';

const WEEK = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const KIND = {
  full: { label: 'All day', short: 'OFF', color: DAY_COLORS.missing, bg: 'var(--rt-cal-absence)', fg: 'var(--rt-danger-strong)' },
  partial: { label: 'Partial', short: '½', color: DAY_COLORS.partial, bg: 'var(--rt-cal-partial)', fg: 'var(--rt-amber)' },
};
const ymOf = (key) => key.slice(0, 7);
const kindOf = (a) => (a ? (a.full_day ? 'full' : 'partial') : null);
const shortDay = (key) => parseLocalDate(key).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const shortMonth = (d) => d.toLocaleDateString('en-GB', { month: 'short' });

function nextWorkday(key) {
  const d = parseLocalDate(key);
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() === 0 || d.getDay() === 6);
  return formatDateYYYYMMDD(d);
}

// Riepilogo compatto: giorni lavorativi consecutivi con la stessa modifica in un solo gruppo (il weekend non interrompe)
function groupChanges(entries) {
  const groups = [];
  entries.forEach(([key, change]) => {
    const last = groups[groups.length - 1];
    if (last && last.change === change && nextWorkday(last.keys[last.keys.length - 1]) === key) last.keys.push(key);
    else groups.push({ change, keys: [key] });
  });
  return groups;
}

function rangeLabel(keys) {
  if (keys.length === 1) return shortDay(keys[0]);
  const a = parseLocalDate(keys[0]);
  const b = parseLocalDate(keys[keys.length - 1]);
  return a.getMonth() === b.getMonth() ? `${a.getDate()}–${b.getDate()} ${shortMonth(b)}` : `${a.getDate()} ${shortMonth(a)} – ${b.getDate()} ${shortMonth(b)}`;
}

function monthGrid(view) {
  const year = view.getFullYear();
  const month = view.getMonth();
  const count = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: count }, (_, i) => {
    const date = new Date(year, month, i + 1);
    return { day: i + 1, key: formatDateYYYYMMDD(date), weekend: date.getDay() === 0 || date.getDay() === 6 };
  });
  return { offset: (new Date(year, month, 1).getDay() + 6) % 7, days };
}

// override.css impone a tutti i dialog larghezza 85% (max 900px) e una posizione centrata: qui serve più stretto,
// fermo in alto (crescendo con il riepilogo, il calendario non si sposta sotto il puntatore) e, sui telefoni, a tutto schermo
const paperSx = (fullScreen) =>
  fullScreen
    ? { width: '100% !important', maxWidth: 'none !important', height: '100%', margin: '0 !important', left: '0 !important', top: '0 !important', transform: 'none !important', borderRadius: 0 }
    : { width: 'calc(100% - 32px) !important', maxWidth: '500px !important', top: '6vh !important', transform: 'translateX(-50%) !important', maxHeight: '88vh', borderRadius: 4 };

function Swatch({ sx, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: 0.75, boxSizing: 'border-box', ...sx }} />
      <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
    </Box>
  );
}

/**
 * Assenze su più giorni: calendario in cui si scelgono i giorni con un clic, trascinando o con Maiusc+clic
 * (intervallo, saltando i weekend), anche su mesi diversi. Ogni giorno prende il tipo scelto in alto
 * (giornata intera o parziale); le assenze già salvate si tolgono o si cambiano allo stesso modo.
 * Il salvataggio è unico: inserimenti in una sola chiamata (aggiorna il tipo se il giorno esiste già), poi le rimozioni.
 */
export default function AbsenceDaysDialog({ open, userId, initialDate, onClose, onSaved }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [view, setView] = useState(() => new Date());
  const [mode, setMode] = useState('full');
  const [existing, setExisting] = useState({}); // "YYYY-MM" → Map(data → assenza)
  const [changes, setChanges] = useState({}); // data → 'full' | 'partial' | 'remove'
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const drag = useRef(null);
  const anchor = useRef(null);

  const ym = formatDateYYYYMMDD(view).slice(0, 7);
  const monthLoaded = !!existing[ym];
  const { offset, days } = monthGrid(view);
  const todayKey = formatDateYYYYMMDD(new Date());

  // Ogni apertura riparte dal mese indicato, senza modifiche
  useEffect(() => {
    if (!open) return;
    const d = initialDate || new Date();
    setView(new Date(d.getFullYear(), d.getMonth(), 1));
    setMode('full');
    setExisting({});
    setChanges({});
    setLoadError(null);
    setSaveError(null);
    anchor.current = null;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !userId || existing[ym]) return undefined;
    let cancelled = false;
    getAbsences(userId, ym)
      .then((list) => {
        if (cancelled) return;
        setExisting((ex) => ({ ...ex, [ym]: new Map(list.map((a) => [String(a.date).slice(0, 10), a])) }));
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(`Unable to load the absences of ${MONTH_NAMES[view.getMonth()]} ${view.getFullYear()}.`);
        setExisting((ex) => ({ ...ex, [ym]: new Map() }));
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId, ym, existing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Il trascinamento finisce ovunque si rilasci il puntatore
  useEffect(() => {
    if (!open) return undefined;
    const stop = () => {
      drag.current = null;
    };
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [open]);

  const existingKind = (key) => kindOf(existing[ymOf(key)]?.get(key));
  const effective = (key) => (changes[key] === 'remove' ? null : changes[key] || existingKind(key));
  // Stato che un clic dà al giorno: il tipo scelto, oppure nessuna assenza se ce l'ha già
  const targetFor = (key) => (effective(key) === mode ? null : mode);

  const withTarget = (base, keys, target) => {
    const next = { ...base };
    keys.forEach((key) => {
      if (target === existingKind(key)) delete next[key];
      else next[key] = target || 'remove';
    });
    return next;
  };
  const applyTo = (keys, target) => setChanges((prev) => withTarget(prev, keys, target));
  // Giorni da un estremo all'altro del mese visibile, senza i weekend (tranne gli estremi, scelti apposta)
  const rangeKeys = (a, b) => {
    const [from, to] = [a, b].sort();
    return days.filter((x) => x.key >= from && x.key <= to && (!x.weekend || x.key === a || x.key === b)).map((x) => x.key);
  };

  const startPaint = (e, d) => {
    if (e.button !== 0 || !monthLoaded || saving) return;
    e.preventDefault();
    if (e.shiftKey && anchor.current && ymOf(anchor.current.key) === ym) {
      applyTo(rangeKeys(anchor.current.key, d.key), anchor.current.target);
      return;
    }
    const target = targetFor(d.key);
    drag.current = { target, start: d.key, base: changes };
    anchor.current = { key: d.key, target };
    applyTo([d.key], target);
  };

  // Trascinando (mouse o dito) prende lo stesso stato tutto l'intervallo dal giorno di partenza a quello sotto il puntatore:
  // nessun giorno saltato se il movimento è veloce, e tornando indietro l'intervallo si accorcia
  const paintMove = (e) => {
    if (!drag.current) return;
    const el = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-day]');
    const d = el && days.find((x) => x.key === el.dataset.day);
    if (!d || d.key === drag.current.last) return;
    drag.current.last = d.key;
    setChanges(withTarget(drag.current.base, rangeKeys(drag.current.start, d.key), drag.current.target));
  };

  const entries = Object.entries(changes).sort(([a], [b]) => a.localeCompare(b));
  const added = entries.filter(([k, c]) => c !== 'remove' && !existingKind(k)).length;
  const changed = entries.filter(([k, c]) => c !== 'remove' && existingKind(k)).length;
  const removed = entries.filter(([, c]) => c === 'remove').length;
  const summary = [
    added && `${added} day${added === 1 ? '' : 's'} to add`,
    changed && `${changed} to change`,
    removed && `${removed} to remove`,
  ]
    .filter(Boolean)
    .join(' · ');

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    const months = [...new Set(entries.map(([k]) => ymOf(k)))];
    const items = entries.filter(([, c]) => c !== 'remove').map(([date, c]) => ({ date, full_day: c === 'full' }));
    const removals = entries.filter(([, c]) => c === 'remove').map(([key]) => existing[ymOf(key)].get(key));
    let done = 0;
    try {
      if (items.length) {
        await addAbsences(userId, items);
        done += items.length;
        setChanges((prev) => Object.fromEntries(Object.entries(prev).filter(([, c]) => c === 'remove')));
      }
      for (const a of removals) {
        await deleteAbsence(a.id);
        done += 1;
        const key = String(a.date).slice(0, 10);
        setChanges(({ [key]: _, ...rest }) => rest);
      }
      onSaved({ months, complete: true, message: `${done} absence change${done === 1 ? '' : 's'} saved` });
    } catch (e) {
      setSaveError(done ? `Saved ${done} of ${entries.length} changes. The others weren’t saved: ${e.message}` : `Not saved: ${e.message}`);
      // Si ricaricano i mesi, così il calendario mostra ciò che è stato salvato davvero
      setExisting({});
      if (done) onSaved({ months, complete: false });
    } finally {
      setSaving(false);
    }
  };

  const dirty = entries.length > 0;
  const close = (_, reason) => {
    if (saving || (dirty && reason === 'backdropClick')) return;
    onClose();
  };

  const cellSx = (d) => {
    const ex = existingKind(d.key);
    const ch = changes[d.key];
    const base = {
      height: 46,
      borderRadius: 2,
      flexDirection: 'column',
      gap: 0.25,
      boxSizing: 'border-box',
      transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 10px var(--rt-hairline)' },
      '&.Mui-focusVisible': { outline: `2px solid ${GREEN}`, outlineOffset: 1 },
    };
    if (ch === 'remove') return { ...base, bgcolor: 'transparent', border: `1.5px dashed ${KIND[ex].color}` };
    if (ch) return { ...base, bgcolor: KIND[ch].bg, border: `2px solid ${KIND[ch].color}` };
    if (ex) return { ...base, bgcolor: KIND[ex].bg, border: '1px solid transparent' };
    if (d.weekend) return { ...base, bgcolor: 'var(--rt-cal-weekend)', border: '1px solid transparent' };
    return { ...base, bgcolor: 'var(--rt-paper)', border: `1px solid ${LINE}` };
  };

  const cellTitle = (d) => {
    const ex = existingKind(d.key);
    const ch = changes[d.key];
    if (ch === 'remove') return `${KIND[ex].label} absence · will be removed`;
    if (ch) return `${KIND[ch].label} absence · not saved yet${ex ? ` (was ${KIND[ex].label.toLowerCase()})` : ''}`;
    if (ex) return `${KIND[ex].label} absence`;
    return '';
  };

  return (
    <Dialog open={open} onClose={close} fullScreen={fullScreen} fullWidth PaperProps={{ sx: paperSx(fullScreen) }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 3, pt: 2.5, pb: 1 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'var(--rt-cal-absence)', color: 'var(--rt-danger-strong)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <EventBusyIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, color: INK, lineHeight: 1.25 }}>Absences</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {fullScreen ? 'Tap or drag across days, weekends excluded.' : 'Click or drag across days. Shift-click selects a range, weekends excluded.'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => close()} disabled={saving} aria-label="Close" sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)} sx={segmentedSx} aria-label="Absence type">
            {Object.entries(KIND).map(([k, kind]) => (
              <ToggleButton key={k} value={k} sx={{ gap: 0.75 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: kind.color }} />
                {kind.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton size="small" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} aria-label="Previous month">
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, color: INK }}>
              {MONTH_NAMES[view.getMonth()]} {view.getFullYear()}
            </Typography>
            {!monthLoaded && <CircularProgress size={14} />}
          </Box>
          <IconButton size="small" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} aria-label="Next month">
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {loadError && (
          <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setLoadError(null)}>
            {loadError}
          </Alert>
        )}

        <Box
          onPointerMove={paintMove}
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.75, touchAction: 'none', userSelect: 'none', opacity: monthLoaded ? 1 : 0.5 }}
        >
          {WEEK.map((w) => (
            <Typography key={w} sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, textAlign: 'center', pb: 0.25 }}>
              {w}
            </Typography>
          ))}
          {Array.from({ length: offset }).map((_, i) => (
            <Box key={`e${i}`} />
          ))}
          {days.map((d) => {
            const eff = effective(d.key);
            const removing = changes[d.key] === 'remove';
            const title = cellTitle(d);
            const cell = (
              <ButtonBase
                key={d.key}
                data-day={d.key}
                disabled={!monthLoaded || saving}
                onPointerDown={(e) => startPaint(e, d)}
                // Solo da tastiera (Invio/Spazio): col mouse il giorno è già stato scelto alla pressione
                onClick={(e) => e.detail === 0 && applyTo([d.key], targetFor(d.key))}
                aria-label={`${shortDay(d.key)}${title ? ` · ${title}` : ''}`}
                aria-pressed={!!eff}
                sx={cellSx(d)}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    lineHeight: 1,
                    fontWeight: d.key === todayKey ? 800 : 600,
                    color: d.key === todayKey ? GREEN : removing ? MUTED : d.weekend && !eff ? 'var(--rt-faint)' : INK,
                    textDecoration: removing ? 'line-through' : 'none',
                  }}
                >
                  {d.day}
                </Typography>
                <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1, minHeight: 10, color: removing ? MUTED : eff ? KIND[eff].fg : 'transparent' }}>
                  {removing ? 'remove' : eff ? KIND[eff].short : ''}
                </Typography>
              </ButtonBase>
            );
            return title ? (
              <Tooltip key={d.key} title={title} arrow disableInteractive>
                <span style={{ display: 'grid' }}>{cell}</span>
              </Tooltip>
            ) : (
              cell
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
          <Swatch sx={{ bgcolor: KIND.full.bg, border: `1px solid ${KIND.full.color}` }} label="All day" />
          <Swatch sx={{ bgcolor: KIND.partial.bg, border: `1px solid ${KIND.partial.color}` }} label="Partial" />
          <Swatch sx={{ border: `2px solid ${MUTED}` }} label="Not saved yet" />
          <Swatch sx={{ border: `1.5px dashed ${MUTED}` }} label="To remove" />
        </Box>

        {dirty && (
          <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${LINE}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK, mb: 1 }}>{summary}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 112, overflowY: 'auto' }}>
              {groupChanges(entries).map(({ change: c, keys }) => {
                const kind = KIND[c === 'remove' ? existingKind(keys[0]) : c];
                return (
                  <Chip
                    key={keys[0]}
                    size="small"
                    label={`${rangeLabel(keys)} · ${c === 'remove' ? 'remove' : kind.label.toLowerCase()}${keys.length > 1 ? ` · ${keys.length} days` : ''}`}
                    onDelete={
                      saving
                        ? undefined
                        : () =>
                            setChanges((prev) => {
                              const next = { ...prev };
                              keys.forEach((k) => delete next[k]);
                              return next;
                            })
                    }
                    sx={{
                      fontWeight: 600,
                      bgcolor: c === 'remove' ? 'transparent' : kind.bg,
                      color: c === 'remove' ? MUTED : kind.fg,
                      border: c === 'remove' ? `1px dashed ${kind.color}` : '1px solid transparent',
                      textDecoration: c === 'remove' ? 'line-through' : 'none',
                      '& .MuiChip-deleteIcon': { color: 'inherit', opacity: 0.6 },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={() => close()} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={save}
          disabled={!dirty || saving}
          sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', bgcolor: GREEN, minWidth: 140 }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : dirty ? `Save ${entries.length} change${entries.length === 1 ? '' : 's'}` : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
